/**
 * Which slot of an Experiment block a response shows, and pruning of the other slot.
 * The page's serverSideProps prunes before dehydrating, so the unshown variant's content is neither
 * embedded in the page data (__NEXT_DATA__) nor prefetched. The Experiment module uses the same rule.
 */
import type { Bucket } from './bucket';
import { env } from '@zopauk/nextjs-env';

// Environments where A/B testing runs. It relies on Fastly assigning the ab_bucket cookie, forwarding it as
// X-AB-Bucket and stripping any client-supplied value (fastly-services#1250), so only enable an environment once
// those rules are deployed for it. `test` is not a deployed environment: it is what the Playwright integration config
// (.env.int) reports in CI, so keep it while we want integration coverage of this path. Production is off until
// prod Fastly is enabled (separate ticket); until then a published Experiment block renders the original, nobody
// is counted, and no page varies on the bucket.
const AB_TESTING_ENVIRONMENTS = ['development', 'test', 'uat'];

type Slots = { active?: boolean | null; treatment: unknown[]; control: unknown[] };

export function pruneExperimentSlots<T extends { __typename: string }>(modules: T[], bucket: Bucket | null): T[] {
  return modules.map((module) => {
    if (module.__typename !== 'ExperimentRecord') {
      return module;
    }

    const { treatment, control, active } = module as unknown as Slots;
    const slot = experimentSlot(active, bucket);
    return {
      ...module,
      treatment: slot === 'treatment' ? treatment : [],
      control: slot === 'control' ? control : [],
    };
  });
}

export function isExperimentActive(active: undefined | boolean | null): boolean {
  // The one rule for "is this experiment running" — serverSideProps (Vary + bucket), slot choice and exposure
  // all use it, so they can't disagree. Only an explicit `false` pauses; null/missing counts as running.
  return active !== false;
}

export function experimentSlot(active: undefined | boolean | null, bucket: undefined | Bucket | null): Bucket {
  return isExperimentActive(active) && bucket === 'treatment' ? 'treatment' : 'control';
}

export function isAbTestingEnabled(): boolean {
  return AB_TESTING_ENVIRONMENTS.includes(env('NEXT_PUBLIC_APP_ENV') ?? '');
}
